/**
 * SigTDD Signup Page
 *
 * Account registration form.
 */

import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import './AuthPages.css';

interface SignupPageProps {
  onClose?: () => void;
  onLogin?: () => void;
  /** Called once the account exists and the user is signed in */
  onSuccess?: () => void;
}

export default function SignupPage({ onClose, onLogin, onSuccess }: SignupPageProps) {
  const { signup } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const name = formData.get('fullName') as string;
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    const result = await signup(email, password, name);

    if (result.success) {
      onSuccess?.();
    } else {
      setError(result.error || 'Failed to create account. Please try again.');
    }

    setLoading(false);
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/sigtdd.svg" alt="" className="auth-logo" />
          <h1>Create an account</h1>
          <p>Get started with SigTDD</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="auth-error">{error}</div>
          )}

          <div className="auth-field">
            <label htmlFor="fullName">Full Name</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              placeholder="John Smith"
              required
              autoComplete="name"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder="••••••••"
              required
              minLength={6}
              autoComplete="new-password"
            />
            <span className="auth-hint">Must be at least 6 characters</span>
          </div>

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <button className="auth-link-btn" onClick={onLogin}>
              Sign in
            </button>
          </p>
        </div>

        {onClose && (
          <div className="auth-back">
            <button className="auth-back-link" onClick={onClose}>
              Back to App
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
